import { useState, useRef, useCallback, useEffect } from 'react';
import { useStickyState } from './useStickyState';

type Direction = 'horizontal' | 'vertical';

interface ResizableOptions {
  initialSize?: number;
  minSize?: number;
  direction?: Direction;
  localStorageKey?: string;
}

export const useResizablePanels = ({
  initialSize = 50,
  minSize = 25,
  direction = 'vertical',
  localStorageKey,
}: ResizableOptions = {}) => {
  const [panelSize, setPanelSize] = useStickyState(initialSize, localStorageKey || 'resizable-panel-size');
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging || !containerRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    let newSizePercent;
    
    if (direction === 'horizontal') {
      const newPrimaryPanelWidth = e.clientX - containerRect.left;
      newSizePercent = (newPrimaryPanelWidth / containerRect.width) * 100;
    } else {
      const newPrimaryPanelHeight = e.clientY - containerRect.top;
      newSizePercent = (newPrimaryPanelHeight / containerRect.height) * 100;
    }

    if (newSizePercent > minSize && newSizePercent < 100 - minSize) {
      setPanelSize(newSizePercent);
    }
  }, [isDragging, minSize, direction, setPanelSize]);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return {
    containerRef,
    panelSize,
    isDragging,
    handleMouseDown,
  };
};
